import React, { useState, useEffect } from "react";
import {
  addAccountingLocalDays,
  formatAccountingLocalDate,
  formatWeekPickerLabel,
  getAccountingWeekStartMonday,
  getWeekNumber,
  parseAccountingLocalDate,
} from "../config/accountingFilterUtils";
import { formatAppIntlLabel, parseDbDateTime } from "../../../utils/appDateTime";

export default function AccountingWeekPicker({ selectedWeek, onWeekChange, weekData }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const anchorDate =
      parseAccountingLocalDate(selectedWeek) ||
      parseAccountingLocalDate(weekData?.start_date) ||
      new Date();
    setCurrentMonth(
      new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1)
    );
  }, [isOpen, selectedWeek, weekData?.start_date]);

  const getWeeksInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startWeek = getAccountingWeekStartMonday(firstDay);

    const weeks = [];
    let current = new Date(startWeek);

    for (let i = 0; i < 6; i++) {
      const weekStart = new Date(current);
      const weekEnd = addAccountingLocalDays(weekStart, 6);

      weeks.push({
        weekNumber: getWeekNumber(weekStart),
        start: new Date(weekStart),
        end: weekEnd,
        startDate: formatAccountingLocalDate(weekStart),
      });

      current.setDate(current.getDate() + 7);
    }

    return weeks;
  };

  const weeks = getWeeksInMonth();

  const handleWeekSelect = (week) => {
    onWeekChange(week.startDate);
    setIsOpen(false);
  };

  const selectedWeekDisplay = selectedWeek
    ? formatWeekPickerLabel(selectedWeek)
    : weekData?.start_date
      ? `Current Week: ${formatWeekPickerLabel(weekData.start_date)}`
      : "Select week...";

  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const handleMonthYearSelect = (month, year) => {
    setCurrentMonth(new Date(year, month, 1));
    setShowMonthYearPicker(false);
  };

  const monthName = formatAppIntlLabel(
    parseDbDateTime(
      `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}-01T12:00:00Z`,
    ),
    { month: "long", year: "numeric" },
  );

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const years = Array.from({ length: 200 }, (_, i) => 1925 + i);

  return (
    <div className="relative">
      <input
        type="text"
        readOnly
        value={selectedWeekDisplay}
        onClick={() => setIsOpen(!isOpen)}
        placeholder="Select week..."
        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-red-500 cursor-pointer"
      />

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-2 w-64">
            {!showMonthYearPicker ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={goToPreviousMonth}
                    className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded"
                    type="button"
                  >
                    ‹
                  </button>
                  <button
                    onClick={() => setShowMonthYearPicker(true)}
                    className="text-xs font-medium hover:bg-gray-100 px-2 py-1 rounded"
                    type="button"
                  >
                    {monthName}
                  </button>
                  <button
                    onClick={goToNextMonth}
                    className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded"
                    type="button"
                  >
                    ›
                  </button>
                </div>

                <div className="grid grid-cols-8 gap-1 mb-1">
                  {["#", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                    (day) => (
                      <div
                        key={day}
                        className="text-center text-[10px] font-semibold text-gray-800"
                      >
                        {day}
                      </div>
                    )
                  )}
                </div>

                <div className="space-y-1">
                  {weeks.map((week, idx) => (
                    <div key={idx} className="grid grid-cols-8 gap-1">
                      <div className="text-center text-[10px] font-medium text-red-600 flex items-center justify-center">
                        W{week.weekNumber}
                      </div>

                      {[0, 1, 2, 3, 4, 5, 6].map((dayOffset) => {
                        const date = addAccountingLocalDays(week.start, dayOffset);
                        const isCurrentMonth =
                          date.getMonth() === currentMonth.getMonth();
                        const isSelected = selectedWeek === week.startDate;

                        return (
                          <button
                            key={dayOffset}
                            type="button"
                            onClick={() => handleWeekSelect(week)}
                            className={`text-[10px] p-1 rounded hover:bg-red-50 ${
                              isSelected
                                ? "bg-red-600 text-white"
                                : isCurrentMonth
                                  ? "text-gray-900"
                                  : "text-gray-400"
                            }`}
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="p-2">
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => setShowMonthYearPicker(false)}
                    className="text-xs text-gray-600 hover:text-gray-800"
                    type="button"
                  >
                    ← Back
                  </button>
                </div>

                <div className="flex gap-2" style={{ height: "200px" }}>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-gray-700 mb-1">
                      Month
                    </div>
                    <div className="overflow-y-auto h-[180px] border border-gray-200 rounded">
                      {months.map((month, idx) => (
                        <button
                          key={month}
                          type="button"
                          onClick={() =>
                            handleMonthYearSelect(
                              idx,
                              currentMonth.getFullYear()
                            )
                          }
                          className={`w-full text-left text-xs py-1.5 px-2 hover:bg-gray-100 ${
                            idx === currentMonth.getMonth()
                              ? "bg-red-600 text-white"
                              : "text-gray-700"
                          }`}
                        >
                          {month}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="text-xs font-medium text-gray-700 mb-1">
                      Year
                    </div>
                    <div className="overflow-y-auto h-[180px] border border-gray-200 rounded">
                      {years.map((year) => (
                        <button
                          key={year}
                          type="button"
                          onClick={() =>
                            handleMonthYearSelect(currentMonth.getMonth(), year)
                          }
                          className={`w-full text-center text-xs py-1.5 px-2 hover:bg-gray-100 ${
                            year === currentMonth.getFullYear()
                              ? "bg-red-600 text-white"
                              : "text-gray-700"
                          }`}
                        >
                          {year}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
